import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { HAMASPIK_LABEL_SX } from "../../../../../assets/hamaspik/theme";

/**
 * A trailing asterisk in a label marks the field required, and it is written into the label text
 * itself — both by hand ("Last name *") and from the field catalogue in PifSectionDynamic. Split
 * off here so it can be coloured, rather than each caller composing its own markup.
 */
const REQUIRED_MARK = /\s*\*\s*$/;

/**
 * The label above a form control. Every section rendered its own copy of this; shared so the
 * required marker can't end up red in some sections and not others.
 */
export const PifFieldLabel: FC<{ label: string }> = ({ label }) => (
  <Typography sx={HAMASPIK_LABEL_SX}>
    {label.replace(REQUIRED_MARK, "")}
    {REQUIRED_MARK.test(label) && (
      // Red, per review — the marker is what tells the user the field is required before they
      // try to submit, and in the label's own brown it read as punctuation.
      <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
        *
      </Box>
    )}
  </Typography>
);

interface PifFieldProps {
  /** End it with " *" to mark the field required. */
  label: string;
  children: React.ReactNode;
  /** Sits beside the label — LOCKED / MASKED and the like. */
  badge?: React.ReactNode;
  /** Why the API refused this value, shown under the control. */
  error?: string;
  /** Static guidance under the control, e.g. an allowed range. */
  hint?: string;
}

const PifField: FC<PifFieldProps> = ({ label, children, badge, error, hint }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {badge ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PifFieldLabel label={label} />
        {badge}
      </Box>
    ) : (
      <PifFieldLabel label={label} />
    )}
    {children}
    {error ? (
      <Typography variant="caption" color="error.main">
        {error}
      </Typography>
    ) : (
      hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )
    )}
  </Box>
);

export default PifField;
